import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { commissionsApi } from "@/api/endpoints";
import type {
  CarrierCommissionStatementDetail,
  CarrierCommissionStatementSummary,
  CommissionLineMatch,
  ProducerCommissionStatement,
  ProducerCommissionSummary,
  ReconciliationKpis,
} from "@/api/types";
import { useAuth } from "@/auth/AuthContext";
import { canAccess } from "@/auth/permissions";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { formatCurrency, formatDate } from "@/utils/format";

type Tab = "mine" | "agency" | "reconcile";

function exceptionLabel(value: string | null): string {
  if (!value) return "—";
  return value.replace(/_/g, " ");
}

function statusClass(status: string): string {
  if (status === "matched" || status === "reconciled") return "comm-status-ok";
  if (status === "resolved" || status === "partial") return "comm-status-warn";
  return "comm-status-bad";
}

export function CommissionsPage() {
  const { user } = useAuth();
  const canViewAgency = canAccess(user, "crm.commissions.view");
  const [tab, setTab] = useState<Tab>(canViewAgency ? "reconcile" : "mine");
  const [myStatement, setMyStatement] = useState<ProducerCommissionStatement | null>(null);
  const [agencySummary, setAgencySummary] = useState<ProducerCommissionSummary | null>(null);
  const [selectedProducerId, setSelectedProducerId] = useState<string | null>(null);
  const [producerStatement, setProducerStatement] = useState<ProducerCommissionStatement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const tasks: Promise<void>[] = [
      commissionsApi
        .myStatement()
        .then(setMyStatement)
        .catch((err) => setError(err instanceof Error ? err.message : "Failed to load your statement")),
    ];
    if (canViewAgency) {
      tasks.push(
        commissionsApi
          .agencySummary()
          .then(setAgencySummary)
          .catch((err) => setError(err instanceof Error ? err.message : "Failed to load agency summary")),
      );
    }
    Promise.all(tasks).finally(() => setLoading(false));
  }, [canViewAgency]);

  useEffect(() => {
    if (!selectedProducerId || !canViewAgency) {
      setProducerStatement(null);
      return;
    }
    commissionsApi
      .producerStatement(selectedProducerId)
      .then(setProducerStatement)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load producer statement"));
  }, [selectedProducerId, canViewAgency]);

  if (loading) {
    return (
      <div className="loading-screen" style={{ minHeight: "12rem" }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Commissions"
        subtitle="Producer statements and carrier remittance reconciliation."
      />
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="tab-row">
        <button type="button" className={`tab-btn${tab === "mine" ? " active" : ""}`} onClick={() => setTab("mine")}>
          My statement
        </button>
        {canViewAgency ? (
          <>
            <button
              type="button"
              className={`tab-btn${tab === "agency" ? " active" : ""}`}
              onClick={() => setTab("agency")}
            >
              Agency rollup
            </button>
            <button
              type="button"
              className={`tab-btn${tab === "reconcile" ? " active" : ""}`}
              onClick={() => setTab("reconcile")}
            >
              Reconciliation
            </button>
          </>
        ) : null}
      </div>

      {tab === "mine" ? <StatementView statement={myStatement} title="My commission statement" /> : null}

      {tab === "agency" && canViewAgency ? (
        <>
          {agencySummary ? (
            <section className="stat-grid">
              <StatCard label="Producers" value={agencySummary.producer_count} />
              <StatCard label="Book premium" value={formatCurrency(agencySummary.total_premium_cents)} />
              <StatCard
                label="Est. commission"
                value={formatCurrency(agencySummary.total_estimated_commission_cents)}
              />
            </section>
          ) : null}

          <div className="detail-grid">
            <div className="card" style={{ padding: "1rem" }}>
              <h3>Producers</h3>
              {!agencySummary || agencySummary.items.length === 0 ? (
                <p className="empty-state">No producer commission data.</p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Producer</th>
                      <th>Policies</th>
                      <th>Premium</th>
                      <th>Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agencySummary.items.map((row) => (
                      <tr
                        key={row.producer_id}
                        style={selectedProducerId === row.producer_id ? { background: "var(--accent-soft)" } : undefined}
                      >
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setSelectedProducerId(row.producer_id)}
                          >
                            {row.display_name}
                          </button>
                        </td>
                        <td>{row.active_policies}</td>
                        <td>{formatCurrency(row.total_premium_cents)}</td>
                        <td>{formatCurrency(row.estimated_commission_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              {producerStatement ? (
                <StatementView statement={producerStatement} title={`${producerStatement.display_name} statement`} />
              ) : (
                <div className="card empty-state" style={{ padding: "1rem" }}>
                  Select a producer to view policy-level statement lines.
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}

      {tab === "reconcile" && canViewAgency ? (
        <ReconciliationPanel onError={setError} />
      ) : null}
    </>
  );
}

function StatementView({
  statement,
  title,
}: {
  statement: ProducerCommissionStatement | null;
  title: string;
}) {
  if (!statement) {
    return (
      <div className="card empty-state" style={{ padding: "1rem" }}>
        No statement data. Assign active policies to producers with premium to see commission estimates.
        <div style={{ marginTop: "0.75rem", fontSize: "0.85rem", color: "var(--muted)" }}>
          Demo: <code>docker compose exec api python scripts/seed_demo_agency.py</code>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <section className="stat-grid">
        <StatCard label="Active policies" value={statement.active_policies} />
        <StatCard label="Premium" value={formatCurrency(statement.total_premium_cents)} />
        <StatCard label="Est. commission" value={formatCurrency(statement.estimated_commission_cents)} />
      </section>

      <div className="card" style={{ padding: "1rem" }}>
        <h3>{title}</h3>
        <p style={{ marginTop: 0, color: "var(--muted)" }}>
          {statement.statement_label} · generated {formatDate(statement.generated_at)}
        </p>
        {statement.lines.length === 0 ? (
          <p className="empty-state">No in-force policies assigned to this producer.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Policy</th>
                <th>Client</th>
                <th>Line</th>
                <th>Premium</th>
                <th>Rate</th>
                <th>Commission</th>
              </tr>
            </thead>
            <tbody>
              {statement.lines.map((line) => (
                <tr key={line.policy_id}>
                  <td>
                    <Link to={`/policies/${line.policy_id}`}>{line.policy_number}</Link>
                  </td>
                  <td>
                    {line.customer_id ? (
                      <Link to={`/clients/${line.customer_id}`}>{line.customer_name}</Link>
                    ) : (
                      line.customer_name
                    )}
                  </td>
                  <td>{line.product_line ?? "—"}</td>
                  <td>{formatCurrency(line.premium_cents)}</td>
                  <td>{line.commission_percent.toFixed(1)}%</td>
                  <td>{formatCurrency(line.estimated_commission_cents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>
                  <strong>Total</strong>
                </td>
                <td>
                  <strong>{formatCurrency(statement.total_premium_cents)}</strong>
                </td>
                <td></td>
                <td>
                  <strong>{formatCurrency(statement.estimated_commission_cents)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

function ReconciliationPanel({ onError }: { onError: (msg: string | null) => void }) {
  const [kpis, setKpis] = useState<ReconciliationKpis | null>(null);
  const [statements, setStatements] = useState<CarrierCommissionStatementSummary[]>([]);
  const [exceptions, setExceptions] = useState<CommissionLineMatch[]>([]);
  const [selected, setSelected] = useState<CarrierCommissionStatementDetail | null>(null);
  const [carrierName, setCarrierName] = useState("Progressive");
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState<string | null>(null);

  const reload = useCallback(() => {
    Promise.all([
      commissionsApi.reconciliationKpis(),
      commissionsApi.listStatements(),
      commissionsApi.listExceptions(true),
    ])
      .then(([kpiData, statementData, exceptionData]) => {
        setKpis(kpiData);
        setStatements(statementData.items);
        setExceptions(exceptionData.items);
        onError(null);
      })
      .catch((err) => onError(err instanceof Error ? err.message : "Failed to load reconciliation"));
  }, [onError]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function onUpload(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      onError("Choose a carrier statement CSV file.");
      return;
    }
    setUploading(true);
    onError(null);
    try {
      const formData = new FormData();
      formData.append("carrier_name", carrierName);
      formData.append("statement_period", period);
      formData.append("file", file);
      const detail = await commissionsApi.uploadStatementCsv(formData);
      setSelected(detail);
      setFile(null);
      reload();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function openStatement(id: string) {
    try {
      const detail = await commissionsApi.getStatement(id);
      setSelected(detail);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to load statement");
    }
  }

  async function postToLedger() {
    if (!selected) return;
    setPosting(true);
    try {
      const result = await commissionsApi.postStatement(selected.id);
      setPostResult(
        `Posted ${result.posted} line${result.posted === 1 ? "" : "s"} (${formatCurrency(result.total_posted_cents)}) to the ledger.`,
      );
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to post to ledger");
    } finally {
      setPosting(false);
    }
  }

  async function resolveException(lineId: string) {
    setResolvingId(lineId);
    try {
      await commissionsApi.resolveLine(lineId, "Reviewed and accepted by accounting.");
      if (selected) {
        const detail = await commissionsApi.getStatement(selected.id);
        setSelected(detail);
      }
      reload();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to resolve exception");
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      {kpis ? (
        <section className="stat-grid">
          <StatCard label="Statements" value={kpis.statement_count} />
          <StatCard label="Matched lines" value={kpis.matched_lines} />
          <StatCard label="Open exceptions" value={kpis.open_exceptions} />
          <StatCard label="Paid vs expected" value={formatCurrency(kpis.total_variance_cents)} />
        </section>
      ) : null}

      <div className="detail-grid">
        <div className="card" style={{ padding: "1rem" }}>
          <h3 style={{ marginTop: 0 }}>Upload carrier statement</h3>
          <p style={{ color: "var(--muted)", fontSize: "0.9rem" }}>
            CSV columns: <code>policy_number</code>, <code>commission</code> (or{" "}
            <code>paid_commission_cents</code>). Optional: <code>premium</code>,{" "}
            <code>insured_name</code>, <code>commission_percent</code>.
          </p>
          <form onSubmit={onUpload} style={{ display: "grid", gap: "0.75rem" }}>
            <label>
              <div className="label">Carrier</div>
              <input value={carrierName} onChange={(e) => setCarrierName(e.target.value)} required />
            </label>
            <label>
              <div className="label">Period (YYYY-MM)</div>
              <input value={period} onChange={(e) => setPeriod(e.target.value)} required pattern="\d{4}-\d{2}" />
            </label>
            <label>
              <div className="label">Statement file</div>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? "Reconciling…" : "Upload & reconcile"}
            </button>
          </form>
        </div>

        <div className="card" style={{ padding: "1rem" }}>
          <h3 style={{ marginTop: 0 }}>Uploaded statements</h3>
          {statements.length === 0 ? (
            <p className="empty-state">No carrier statements uploaded yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Carrier</th>
                  <th>Period</th>
                  <th>Status</th>
                  <th>Matched</th>
                  <th>Exceptions</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {statements.map((row) => (
                  <tr key={row.id}>
                    <td>{row.carrier_name}</td>
                    <td>{row.statement_period}</td>
                    <td>
                      <span className={`comm-status-badge ${statusClass(row.status)}`}>{row.status}</span>
                    </td>
                    <td>
                      {row.matched_count}/{row.line_count}
                    </td>
                    <td>{row.exception_count}</td>
                    <td>
                      <button type="button" className="btn btn-ghost" onClick={() => openStatement(row.id)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: "1rem" }}>
        <h3 style={{ marginTop: 0 }}>Exception queue</h3>
        {exceptions.length === 0 ? (
          <p className="empty-state">No open exceptions — all uploaded lines matched or resolved.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Policy</th>
                <th>Classification</th>
                <th>Paid</th>
                <th>Expected</th>
                <th>Variance</th>
                <th>Rationale</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {exceptions.map((line) => (
                <tr key={line.id}>
                  <td>
                    {line.policy_id ? (
                      <Link to={`/policies/${line.policy_id}`}>{line.policy_number_raw}</Link>
                    ) : (
                      line.policy_number_raw
                    )}
                  </td>
                  <td>
                    <span className={`comm-status-badge ${statusClass(line.match_status)}`}>
                      {exceptionLabel(line.exception_class)}
                    </span>
                  </td>
                  <td>{formatCurrency(line.paid_commission_cents)}</td>
                  <td>
                    {line.expected_commission_cents != null
                      ? formatCurrency(line.expected_commission_cents)
                      : "—"}
                  </td>
                  <td>
                    {line.variance_cents != null ? formatCurrency(line.variance_cents) : "—"}
                  </td>
                  <td style={{ maxWidth: "18rem", fontSize: "0.85rem" }}>{line.exception_rationale}</td>
                  <td>
                    <button
                      type="button"
                      className="btn"
                      disabled={resolvingId === line.id}
                      onClick={() => resolveException(line.id)}
                    >
                      {resolvingId === line.id ? "…" : "Resolve"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected ? (
        <div className="card" style={{ padding: "1rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
            <div>
              <h3 style={{ marginTop: 0 }}>
                {selected.carrier_name} · {selected.statement_period}
              </h3>
              <p style={{ color: "var(--muted)", marginTop: 0 }}>
                {selected.matched_count} matched · {selected.exception_count} exceptions · variance{" "}
                {formatCurrency(selected.variance_cents)}
              </p>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
              <button type="button" className="btn btn-primary" onClick={postToLedger} disabled={posting}>
                {posting ? "Posting…" : "Post to ledger"}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
          </div>
          {postResult ? (
            <p style={{ color: "var(--accent-deep, #0b6f63)", fontSize: "0.9rem" }}>{postResult}</p>
          ) : null}
          <table className="data-table">
            <thead>
              <tr>
                <th>Policy</th>
                <th>Status</th>
                <th>Paid</th>
                <th>Expected</th>
                <th>Class</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {selected.lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.policy_number_raw}</td>
                  <td>
                    <span className={`comm-status-badge ${statusClass(line.match_status)}`}>
                      {line.match_status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td>{formatCurrency(line.paid_commission_cents)}</td>
                  <td>
                    {line.expected_commission_cents != null
                      ? formatCurrency(line.expected_commission_cents)
                      : "—"}
                  </td>
                  <td>{exceptionLabel(line.exception_class)}</td>
                  <td>
                    {line.confidence != null ? `${(line.confidence * 100).toFixed(0)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
